import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Square, 
  Trash2, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Database,
  MapPin,
  Car,
  Users,
  Settings,
  BarChart3
} from 'lucide-react';
import importService from '../../services/importService';
import { 
  IMPORT_STATUS, 
  STATUS_COLORS, 
  STATUS_LABELS, 
  DEFAULT_CONFIG,
  STATE_CODES,
  formatNumber,
  formatDuration,
  calculateProgress,
  getStatusIcon,
  validateStates,
  validateNumericInput,
  VALIDATION_RULES
} from '../../types/import';

// Styled Components
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #ffffff;
  padding: 0;
`;

const Header = styled.header`
  background: #1a1a1a;
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

const DashboardTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const RefreshButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    cursor: not-allowed;
    transform: none;
    opacity: 0.5;
  }
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.accentColor || '#F43F60'};
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 12px;
  color: ${props => props.color || '#F43F60'};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const ControlsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ControlCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ControlTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 16px;
  color: #ffffff;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F43F60;
    background: rgba(255, 255, 255, 0.08);
  }
  
  &:invalid {
    border-color: #e53e3e;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 16px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F43F60;
    background: rgba(255, 255, 255, 0.08);
  }
  
  option {
    background: #1a1a1a;
    color: #ffffff;
  }
`;

const MultiSelect = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  input[type="checkbox"] {
    margin: 0;
    accent-color: #F43F60;
  }
`;

const Button = styled.button`
  background: ${props => props.variant === 'danger' ? '#e53e3e' : props.variant === 'success' ? '#38a169' : '#F43F60'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: ${props => props.variant === 'danger' ? '#c53030' : props.variant === 'success' ? '#2f855a' : '#E03A56'};
    transform: translateY(-1px);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
    transform: none;
    opacity: 0.5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ProgressTable = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const TableHeader = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TableTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: rgba(255, 255, 255, 0.05);
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => STATUS_COLORS[props.status] || '#F43F60'};
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => STATUS_COLORS[props.status] || '#F43F60'};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ErrorMessage = styled.div`
  background: rgba(229, 62, 62, 0.1);
  border: 1px solid rgba(229, 62, 62, 0.3);
  color: #f56565;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SuccessMessage = styled.div`
  background: rgba(56, 161, 105, 0.1);
  border: 1px solid rgba(56, 161, 105, 0.3);
  color: #68d391;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top: 2px solid #F43F60;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ImportDashboard = () => {
  // State management
  const [summary, setSummary] = useState({
    totalStates: 0,
    runningStates: 0,
    completedStates: 0,
    errorStates: 0,
    totalDealersProcessed: 0,
    totalVehiclesProcessed: 0
  });
  const [states, setStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [singleStateForm, setSingleStateForm] = useState({
    stateCode: '',
    maxDealers: DEFAULT_CONFIG.MAX_DEALERS,
    vehiclesPerDealer: DEFAULT_CONFIG.VEHICLES_PER_DEALER
  });
  const [multiStateForm, setMultiStateForm] = useState({
    selectedStates: [],
    maxDealersPerState: DEFAULT_CONFIG.MAX_DEALERS,
    vehiclesPerDealer: DEFAULT_CONFIG.VEHICLES_PER_DEALER
  });
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Fetch progress data
  const fetchProgress = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const data = await importService.getProgressSummary();
      setSummary(data);
      setStates(data.states);
      setError('');
    } catch (err) {
      setError(`Failed to fetch progress: ${err.message}`);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    fetchProgress();
    
    if (autoRefresh) {
      const interval = setInterval(fetchProgress, DEFAULT_CONFIG.AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [fetchProgress, autoRefresh]);

  // Update current time for elapsed time calculation
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second for live elapsed time

    return () => clearInterval(timeInterval);
  }, []);

  // Handle single state import
  const handleSingleStateImport = async () => {
    if (!singleStateForm.stateCode) {
      setError('Please select a state');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await importService.importSingleState(
        singleStateForm.stateCode,
        singleStateForm.maxDealers,
        singleStateForm.vehiclesPerDealer
      );
      
      setSuccess(`Import started for state ${singleStateForm.stateCode}`);
      await fetchProgress();
    } catch (err) {
      setError(`Failed to start import: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle multiple states import
  const handleMultiStateImport = async () => {
    if (multiStateForm.selectedStates.length === 0) {
      setError('Please select at least one state');
      return;
    }

    const validation = validateStates(multiStateForm.selectedStates);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await importService.importMultipleStates(
        multiStateForm.selectedStates,
        multiStateForm.maxDealersPerState,
        multiStateForm.vehiclesPerDealer
      );
      
      setSuccess(`Import started for states: ${multiStateForm.selectedStates.join(', ')}`);
      await fetchProgress();
    } catch (err) {
      setError(`Failed to start import: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear all checkpoints
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all checkpoints? This will reset all progress.')) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await importService.clearAllCheckpoints();
      setSuccess('All checkpoints cleared successfully');
      await fetchProgress();
    } catch (err) {
      setError(`Failed to clear checkpoints: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear single state checkpoint
  const handleClearState = async (stateCode) => {
    if (!window.confirm(`Are you sure you want to clear the checkpoint for state ${stateCode}?`)) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await importService.clearStateCheckpoint(stateCode);
      setSuccess(`Checkpoint cleared for state ${stateCode}`);
      await fetchProgress();
    } catch (err) {
      setError(`Failed to clear checkpoint: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle state selection for multi-state form
  const handleStateToggle = (stateCode) => {
    setMultiStateForm(prev => ({
      ...prev,
      selectedStates: prev.selectedStates.includes(stateCode)
        ? prev.selectedStates.filter(s => s !== stateCode)
        : [...prev.selectedStates, stateCode]
    }));
  };

  // Format date and time for display
  const formatDateTime = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleString('en-US', { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false
    });
  };

  // Calculate elapsed time
  const calculateElapsedTime = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : currentTime;
    const diffMs = end - start;
    
    if (diffMs < 0) return '0s';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <DashboardContainer>
      <Header>
        <HeaderContent>
          <DashboardTitle>
            <Database size={40} />
            Import Dashboard
          </DashboardTitle>
          <RefreshButton 
            onClick={fetchProgress} 
            disabled={isRefreshing}
          >
            {isRefreshing ? <LoadingSpinner /> : <RefreshCw size={20} />}
            Refresh
          </RefreshButton>
        </HeaderContent>
      </Header>

      <MainContent>

        {/* Messages */}
      {error && (
        <ErrorMessage>
            <AlertCircle size={20} />
            {error}
        </ErrorMessage>
      )}
        {success && (
          <SuccessMessage>
            <CheckCircle size={20} />
            {success}
          </SuccessMessage>
        )}

        {/* Stats Grid */}
        <StatsGrid>
          <StatCard color="#e6fffa" accentColor="#38a169">
            <StatIcon color="#38a169">
              <MapPin size={40} />
            </StatIcon>
            <StatValue>{summary.totalStates}</StatValue>
            <StatLabel>Total States</StatLabel>
          </StatCard>
          
          <StatCard color="#ebf8ff" accentColor="#3182ce">
            <StatIcon color="#3182ce">
              <TrendingUp size={40} />
            </StatIcon>
            <StatValue>{summary.runningStates}</StatValue>
            <StatLabel>Running</StatLabel>
          </StatCard>
          
          <StatCard color="#f0fff4" accentColor="#38a169">
            <StatIcon color="#38a169">
              <CheckCircle size={40} />
            </StatIcon>
            <StatValue>{summary.completedStates}</StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
          
          <StatCard color="#fed7d7" accentColor="#e53e3e">
            <StatIcon color="#e53e3e">
              <AlertCircle size={40} />
            </StatIcon>
            <StatValue>{summary.errorStates}</StatValue>
            <StatLabel>Errors</StatLabel>
          </StatCard>
          
          <StatCard color="#faf5ff" accentColor="#805ad5">
            <StatIcon color="#805ad5">
              <Users size={40} />
            </StatIcon>
            <StatValue>{formatNumber(summary.totalDealersProcessed)}</StatValue>
            <StatLabel>Dealers Processed</StatLabel>
          </StatCard>
          
          <StatCard color="#fff5f5" accentColor="#f56565">
            <StatIcon color="#f56565">
              <Car size={40} />
            </StatIcon>
            <StatValue>{formatNumber(summary.totalVehiclesProcessed)}</StatValue>
            <StatLabel>Vehicles Processed</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Controls Section */}
        <ControlsSection>
          {/* Single State Import */}
          <ControlCard>
            <ControlTitle>
              <Play size={24} />
              Single State Import
            </ControlTitle>
            
            <FormGroup>
              <Label>State Code</Label>
              <Select
                value={singleStateForm.stateCode}
                onChange={(e) => setSingleStateForm(prev => ({ ...prev, stateCode: e.target.value }))}
              >
                <option value="">Select State</option>
                {STATE_CODES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Max Dealers</Label>
              <Input
                type="number"
                min={VALIDATION_RULES.MAX_DEALERS.MIN}
                max={VALIDATION_RULES.MAX_DEALERS.MAX}
                value={singleStateForm.maxDealers}
                onChange={(e) => setSingleStateForm(prev => ({ ...prev, maxDealers: parseInt(e.target.value) || 0 }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Vehicles per Dealer</Label>
              <Input
                type="number"
                min={VALIDATION_RULES.VEHICLES_PER_DEALER.MIN}
                max={VALIDATION_RULES.VEHICLES_PER_DEALER.MAX}
                value={singleStateForm.vehiclesPerDealer}
                onChange={(e) => setSingleStateForm(prev => ({ ...prev, vehiclesPerDealer: parseInt(e.target.value) || 0 }))}
              />
            </FormGroup>
            
            <Button 
              onClick={handleSingleStateImport}
              disabled={isLoading || !singleStateForm.stateCode}
            >
              {isLoading ? <LoadingSpinner /> : <Play size={20} />}
            Start Import
            </Button>
          </ControlCard>

          {/* Multiple States Import */}
          <ControlCard>
            <ControlTitle>
              <Play size={24} />
              Multiple States Import
            </ControlTitle>
            
            <FormGroup>
              <Label>Select States</Label>
              <MultiSelect>
                {STATE_CODES.map(state => (
                  <CheckboxLabel key={state}>
                    <input
                      type="checkbox"
                      checked={multiStateForm.selectedStates.includes(state)}
                      onChange={() => handleStateToggle(state)}
                    />
                    {state}
                  </CheckboxLabel>
                ))}
              </MultiSelect>
            </FormGroup>
            
            <FormGroup>
              <Label>Max Dealers per State</Label>
              <Input
                type="number"
                min={VALIDATION_RULES.MAX_DEALERS.MIN}
                max={VALIDATION_RULES.MAX_DEALERS.MAX}
                value={multiStateForm.maxDealersPerState}
                onChange={(e) => setMultiStateForm(prev => ({ ...prev, maxDealersPerState: parseInt(e.target.value) || 0 }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Vehicles per Dealer</Label>
              <Input
                type="number"
                min={VALIDATION_RULES.VEHICLES_PER_DEALER.MIN}
                max={VALIDATION_RULES.VEHICLES_PER_DEALER.MAX}
                value={multiStateForm.vehiclesPerDealer}
                onChange={(e) => setMultiStateForm(prev => ({ ...prev, vehiclesPerDealer: parseInt(e.target.value) || 0 }))}
              />
            </FormGroup>
            
            <Button 
              onClick={handleMultiStateImport}
              disabled={isLoading || multiStateForm.selectedStates.length === 0}
            >
              {isLoading ? <LoadingSpinner /> : <Play size={20} />}
              Start Import
            </Button>
          </ControlCard>
        </ControlsSection>

        {/* Progress Table */}
        <ProgressTable>
          <TableHeader>
            <TableTitle>
              <TrendingUp size={24} />
              Import Progress
            </TableTitle>
          </TableHeader>
          
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>State</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Dealers</TableHeaderCell>
                <TableHeaderCell>Vehicles</TableHeaderCell>
                <TableHeaderCell>Progress</TableHeaderCell>
                <TableHeaderCell>Start Time</TableHeaderCell>
                <TableHeaderCell>End Time</TableHeaderCell>
                <TableHeaderCell>Elapsed</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {Object.entries(states).reverse().map(([stateCode, progress]) => (
                <TableRow key={stateCode}>
                  <TableCell>
                    <strong>{stateCode}</strong>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={progress.status}>
                      {getStatusIcon(progress.status)}
                      {STATUS_LABELS[progress.status]}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {formatNumber(progress.dealersProcessed)} / {formatNumber(progress.totalDealers)}
                  </TableCell>
                  <TableCell>
                    {formatNumber(progress.vehiclesProcessed)} 
                  </TableCell>
                  <TableCell>
                    <ProgressBar>
                      <ProgressFill 
                        progress={calculateProgress(progress.dealersProcessed, progress.totalDealers)}
                        status={progress.status}
                      />
                    </ProgressBar>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      {calculateProgress(progress.dealersProcessed, progress.totalDealers)}%
                    </div>
                  </TableCell>
                  <TableCell style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace', fontSize: '12px' }}>
                    {formatDateTime(progress.startTime)}
                  </TableCell>
                  <TableCell style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace', fontSize: '12px' }}>
                    {formatDateTime(progress.endTime)}
                  </TableCell>
                  <TableCell style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace', fontSize: '12px', fontWeight: '600' }}>
                    {calculateElapsedTime(progress.startTime, progress.endTime)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ProgressTable>

       
      </MainContent>
    </DashboardContainer>
  );
};

export default ImportDashboard;
